// pages/secure/IntranetPage.js
// The brokerage's internal intranet: notices, a knowledge base, industry news,
// and the office link shelf. Everyone reads; the managing broker and office
// managers publish.
//
// Laid out as a workplace reference tool rather than a feed: pinned notices sit
// above everything, dates run in a fixed left rail so the eye can scan them, and
// nothing is hidden behind a hover. Categories carry meaning here — Compliance is
// deliberately the one color that stands out, because those are the notices that
// carry a deadline.

import React, { useCallback, useEffect, useState } from 'react';
import { useApiClient } from '../../hooks/useApiClient';
import PortalHome from '../../components/secure/PortalHome';
import Modal from '../../components/secure/Modal';

const ANN_CATS = ['General', 'Compliance', 'Operations', 'Training', 'HR', 'Technology'];
const ART_CATS = ['How-To', 'Policy', 'Onboarding', 'Compliance', 'Systems', 'Marketing', 'Other'];
const LINK_CATS = ['Regulatory', 'Industry News', 'Education', 'Tools', 'Brokerage', 'Reference'];

// Compliance is the only saturated tone. Everything else stays quiet so that a
// deadline notice is visibly different from an office memo at a glance.
const CAT_TONE = {
    Compliance: 'bg-amber-50 text-amber-800 ring-amber-200',
    Operations: 'bg-blue-50 text-blue-700 ring-blue-200',
    Training: 'bg-violet-50 text-violet-700 ring-violet-200',
    HR: 'bg-rose-50 text-rose-700 ring-rose-200',
    Technology: 'bg-teal-50 text-teal-700 ring-teal-200',
    General: 'bg-slate-50 text-slate-600 ring-slate-200',
};

const fmtDate = (d) => d
    ? new Date(d).toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' })
    : '';

// Relative age, for news headlines where "3h ago" reads faster than a date.
const ago = (d) => {
    if (!d) return '';
    const mins = Math.round((Date.now() - new Date(d)) / 60000);
    if (mins < 60) return `${Math.max(mins, 1)}m ago`;
    if (mins < 1440) return `${Math.round(mins / 60)}h ago`;
    const days = Math.round(mins / 1440);
    return days < 8 ? `${days}d ago` : fmtDate(d);
};

const Tag = ({ children }) => (
    <span className={`inline-flex items-center px-2 py-0.5 rounded text-[11px] font-medium ring-1 ring-inset whitespace-nowrap ${CAT_TONE[children] || CAT_TONE.General}`}>
        {children}
    </span>
);

const StatusTag = ({ status }) => status === 'published' ? null : (
    <span className="inline-flex items-center px-2 py-0.5 rounded text-[11px] font-medium ring-1 ring-inset bg-yellow-50 text-yellow-800 ring-yellow-200 uppercase tracking-wide">
        {status}
    </span>
);

const Eyebrow = ({ children, right }) => (
    <div className="flex items-baseline justify-between mb-2">
        <h2 className="text-[11px] font-semibold uppercase tracking-[0.08em] text-slate-500">{children}</h2>
        {right}
    </div>
);

const Panel = ({ children, className = '' }) => (
    <section className={`bg-white border border-slate-200 rounded ${className}`}>{children}</section>
);

const IntranetPage = ({ user }) => {
    const api = useApiClient();
    const canPublish = !!(user?.isManagingBroker || user?.staffRole === 'Office Manager');

    const [tab, setTab] = useState('home');
    const [home, setHome] = useState(null);
    const [announcements, setAnnouncements] = useState([]);
    const [articles, setArticles] = useState([]);
    const [links, setLinks] = useState([]);
    const [news, setNews] = useState(null);
    const [newsLoading, setNewsLoading] = useState(false);
    const [feeds, setFeeds] = useState([]);

    const [openArticle, setOpenArticle] = useState(null);
    const [openAnn, setOpenAnn] = useState(null);
    const [annForm, setAnnForm] = useState(null);
    const [artForm, setArtForm] = useState(null);
    const [linkForm, setLinkForm] = useState(null);
    const [feedForm, setFeedForm] = useState(null);
    const [manageFeeds, setManageFeeds] = useState(false);

    const [q, setQ] = useState('');
    const [kbCat, setKbCat] = useState('');
    const [error, setError] = useState('');
    const [saving, setSaving] = useState(false);

    const loadNews = useCallback(() => {
        setNewsLoading(true);
        api.get('/api/intranet/news')
            .then(setNews)
            // Record the failure *as* the news state. Leaving it null would let the
            // bootstrap effect below see "no news yet" and retry forever.
            .catch(e => setNews({ items: [], feeds: [], errors: [{ label: 'News service', message: e.message }] }))
            .finally(() => setNewsLoading(false));
    }, [api]);

    const load = useCallback(() => {
        const fail = e => setError(e.message);
        if (tab === 'home') api.get('/api/intranet/home').then(setHome).catch(fail);
        if (tab === 'notices') api.get('/api/intranet/announcements').then(setAnnouncements).catch(fail);
        if (tab === 'kb') {
            const params = new URLSearchParams();
            if (q) params.set('q', q);
            if (kbCat) params.set('category', kbCat);
            const qs = params.toString();
            api.get(`/api/intranet/articles${qs ? `?${qs}` : ''}`).then(setArticles).catch(fail);
        }
        if (tab === 'links') api.get('/api/intranet/links').then(setLinks).catch(fail);
    }, [api, tab, q, kbCat]);

    useEffect(() => { load(); }, [load]);

    // Headlines are fetched once per visit and shared by the home rail and the
    // News tab, rather than re-fetched whenever either one re-renders.
    useEffect(() => {
        if ((tab === 'home' || tab === 'news') && !news && !newsLoading) loadNews();
    }, [tab, news, newsLoading, loadNews]);

    const openFeedManager = async () => {
        setError('');
        try {
            setFeeds(await api.get('/api/intranet/feeds'));
            setManageFeeds(true);
        } catch (e) { setError(e.message); }
    };

    const save = async (kind) => {
        const form = { ann: annForm, art: artForm, link: linkForm, feed: feedForm }[kind];
        const base = {
            ann: '/api/intranet/announcements', art: '/api/intranet/articles',
            link: '/api/intranet/links', feed: '/api/intranet/feeds',
        }[kind];
        const id = form.announcement_id || form.article_id || form.link_id;
        setSaving(true); setError('');
        try {
            if (id) await api.put(`${base}/${id}`, form);
            else await api.post(base, form);
            setAnnForm(null); setArtForm(null); setLinkForm(null); setFeedForm(null);
            if (kind === 'feed') { setFeeds(await api.get('/api/intranet/feeds')); setNews(null); loadNews(); }
            else load();
        } catch (e) { setError(e.message); } finally { setSaving(false); }
    };

    const remove = async (base, id, label) => {
        if (!window.confirm(`Remove "${label}"?`)) return;
        try {
            await api.delete(`/api/intranet/${base}/${id}`);
            if (base === 'feeds') { setFeeds(await api.get('/api/intranet/feeds')); setNews(null); loadNews(); }
            else load();
        } catch (e) { setError(e.message); }
    };

    const readArticle = async (id) => {
        try { setOpenArticle(await api.get(`/api/intranet/articles/${id}`)); } catch (e) { setError(e.message); }
    };

    const input = 'block w-full px-3 py-2 border border-slate-300 rounded text-sm focus:outline-none focus:ring-1 focus:ring-slate-500 focus:border-slate-500';
    const label = 'block text-xs font-medium text-slate-600 mb-1';
    const btn = 'text-sm px-3 py-1.5 bg-slate-800 text-white rounded hover:bg-slate-900 focus:outline-none focus:ring-2 focus:ring-slate-500 focus:ring-offset-1';
    const btnGhost = 'text-sm px-3 py-1.5 border border-slate-300 text-slate-700 rounded hover:bg-slate-50';

    const TabBtn = ({ id, children, count }) => (
        <button onClick={() => setTab(id)}
            className={`px-3 py-2.5 text-sm font-medium border-b-2 -mb-px transition-colors ${
                tab === id
                    ? 'border-slate-700 text-indigo-700'
                    : 'border-transparent text-slate-500 hover:text-slate-800 hover:border-slate-300'}`}>
            {children}
            {count > 0 && <span className="ml-1.5 text-[11px] text-slate-400 tabular-nums">{count}</span>}
        </button>
    );


    const NewsItem = ({ n }) => (
        <a href={n.url} target="_blank" rel="noopener noreferrer"
            className="block px-4 py-3 hover:bg-slate-50 group">
            <div className="flex items-baseline gap-2 flex-wrap">
                <span className="text-[11px] font-semibold uppercase tracking-wide text-slate-400">{n.source}</span>
                <span className="text-[11px] text-slate-400 tabular-nums">{ago(n.published_at)}</span>
            </div>
            <div className="font-medium text-slate-800 group-hover:text-indigo-700 mt-0.5">{n.title}</div>
            {n.summary && <p className="text-[13px] text-slate-500 mt-1 leading-relaxed">{n.summary}</p>}
        </a>
    );

    return (
        <div className="p-6 max-w-6xl">
            {/* masthead */}
            <header className="pb-4 border-b border-slate-200">
                <div className="flex items-start justify-between gap-4 flex-wrap">
                    <div>
                        <h1 className="text-2xl font-bold text-slate-900 tracking-tight">Office Intranet</h1>
                        <p className="text-sm text-slate-500 mt-0.5">
                            Notices, office practice, and industry news for Business Real Estate Corp.
                        </p>
                    </div>
                    {canPublish && (
                        <span className="text-[11px] font-medium uppercase tracking-wide text-slate-400 border border-slate-200 rounded px-2 py-1">
                            Publisher access
                        </span>
                    )}
                </div>
            </header>

            <nav className="flex gap-5 border-b border-slate-200 mb-5 overflow-x-auto">
                <TabBtn id="home">Home</TabBtn>
                <TabBtn id="notices">Notices</TabBtn>
                <TabBtn id="kb">Knowledge Base</TabBtn>
                <TabBtn id="news">Industry News</TabBtn>
                <TabBtn id="links">Quick Links</TabBtn>
            </nav>

            {error && (
                <div className="mb-4 px-3 py-2 rounded border border-red-200 bg-red-50 text-sm text-red-700">{error}</div>
            )}

            {/* ---------------- HOME ---------------- */}
            {tab === 'home' && !home && <p className="text-sm text-slate-400 py-10 text-center">Loading…</p>}
            {tab === 'home' && home && (
                <PortalHome
                    user={user}
                    announcements={home.announcements || []}
                    articles={home.articles || []}
                    links={home.links || []}
                    news={news || []}
                    onOpenAnnouncement={setOpenAnn}
                />
            )}

            {/* ---------------- NOTICES ---------------- */}
            {tab === 'notices' && (
                <div className="space-y-4">
                    {canPublish && (
                        <div className="flex justify-end">
                            <button className={btn}
                                onClick={() => setAnnForm({ title: '', body_html: '', category: 'General', status: 'published', is_pinned: false })}>
                                New notice
                            </button>
                        </div>
                    )}
                    {announcements.map(a => (
                        <article key={a.announcement_id} className="bg-white border border-slate-200 rounded">
                            <div className="px-5 py-3 border-b border-slate-100 flex justify-between items-start gap-3 flex-wrap">
                                <div className="min-w-0">
                                    <div className="flex items-center gap-2 flex-wrap">
                                        {!!a.is_pinned && (
                                            <span className="text-[10px] font-bold uppercase tracking-[0.08em] text-amber-700 bg-amber-100 px-1.5 py-0.5 rounded">Pinned</span>
                                        )}
                                        <Tag>{a.category}</Tag>
                                        <StatusTag status={a.status} />
                                    </div>
                                    <h3 className="font-semibold text-slate-900 mt-1.5">{a.title}</h3>
                                    <div className="text-[11px] text-slate-400 mt-0.5 tabular-nums">
                                        {fmtDate(a.published_at || a.created_at)}{a.author ? ` · ${a.author}` : ''}
                                    </div>
                                </div>
                                {canPublish && (
                                    <div className="text-sm whitespace-nowrap flex gap-3">
                                        <button onClick={() => setAnnForm({ ...a, is_pinned: !!a.is_pinned })} className="text-indigo-600 hover:underline">Edit</button>
                                        <button onClick={() => remove('announcements', a.announcement_id, a.title)} className="text-red-600 hover:underline">Remove</button>
                                    </div>
                                )}
                            </div>
                            <div className="doc-body"
                                dangerouslySetInnerHTML={{ __html: a.body_html || '' }} />
                        </article>
                    ))}
                    {!announcements.length && (
                        <p className="text-center text-slate-400 py-12 text-sm">No notices yet.</p>
                    )}
                </div>
            )}

            {/* ---------------- KNOWLEDGE BASE ---------------- */}
            {tab === 'kb' && (
                <div className="space-y-4">
                    <div className="flex justify-between items-center gap-3 flex-wrap">
                        <input className={`${input} max-w-xs`} placeholder="Search guides…" value={q}
                            onChange={e => setQ(e.target.value)} />
                        {canPublish && (
                            <button className={btn}
                                onClick={() => setArtForm({ title: '', body_html: '', category: 'How-To', tags: '', status: 'published' })}>
                                New guide
                            </button>
                        )}
                    </div>

                    <div className="flex gap-1.5 flex-wrap">
                        <button onClick={() => setKbCat('')}
                            className={`px-2.5 py-1 rounded text-xs font-medium border ${!kbCat ? 'bg-slate-800 text-white border-slate-800' : 'border-slate-200 text-slate-600 hover:bg-slate-50'}`}>
                            All
                        </button>
                        {ART_CATS.map(c => (
                            <button key={c} onClick={() => setKbCat(kbCat === c ? '' : c)}
                                className={`px-2.5 py-1 rounded text-xs font-medium border ${kbCat === c ? 'bg-slate-800 text-white border-slate-800' : 'border-slate-200 text-slate-600 hover:bg-slate-50'}`}>
                                {c}
                            </button>
                        ))}
                    </div>

                    <Panel>
                        <div className="divide-y divide-slate-100">
                            {articles.map(a => (
                                <div key={a.article_id} className="px-4 py-3 flex justify-between items-center gap-3 hover:bg-slate-50">
                                    <button onClick={() => readArticle(a.article_id)} className="text-left min-w-0 flex-1 group">
                                        <div className="flex items-center gap-2 flex-wrap">
                                            <span className="font-medium text-slate-800 group-hover:text-indigo-700">{a.title}</span>
                                            <StatusTag status={a.status} />
                                        </div>
                                        <div className="text-[11px] text-slate-400 mt-0.5">
                                            {a.category}{a.tags ? ` · ${a.tags}` : ''} · <span className="tabular-nums">{a.view_count}</span> views
                                        </div>
                                    </button>
                                    {canPublish && (
                                        <div className="text-sm whitespace-nowrap flex gap-3">
                                            <button onClick={async () => setArtForm(await api.get(`/api/intranet/articles/${a.article_id}`))}
                                                className="text-indigo-600 hover:underline">Edit</button>
                                            <button onClick={() => remove('articles', a.article_id, a.title)}
                                                className="text-red-600 hover:underline">Remove</button>
                                        </div>
                                    )}
                                </div>
                            ))}
                            {!articles.length && (
                                <p className="px-4 py-12 text-center text-slate-400 text-sm">
                                    {q || kbCat ? 'No guides match that search.' : 'No guides yet.'}
                                </p>
                            )}
                        </div>
                    </Panel>
                </div>
            )}

            {/* ---------------- INDUSTRY NEWS ---------------- */}
            {tab === 'news' && (
                <div className="space-y-4">
                    <div className="flex justify-between items-center gap-3 flex-wrap">
                        <p className="text-[13px] text-slate-500">
                            Headlines from the trade press. Stories open on the publisher's site.
                        </p>
                        <div className="flex gap-2">
                            <button onClick={loadNews} className={btnGhost} disabled={newsLoading}>
                                {newsLoading ? 'Refreshing…' : 'Refresh'}
                            </button>
                            {canPublish && <button onClick={openFeedManager} className={btnGhost}>Manage sources</button>}
                        </div>
                    </div>

                    {!!news?.errors?.length && (
                        <div className="px-3 py-2 rounded border border-amber-200 bg-amber-50 text-[13px] text-amber-800">
                            {news.errors.map((e, i) => (
                                <div key={i}>
                                    <strong>{e.label}</strong> could not be reached ({e.message}).
                                    {e.served_stale ? ' Showing the last headlines we retrieved.' : ''}
                                </div>
                            ))}
                        </div>
                    )}

                    <Panel>
                        <div className="divide-y divide-slate-100">
                            {newsLoading && !news && <p className="px-4 py-12 text-center text-slate-400 text-sm">Loading headlines…</p>}
                            {(news?.items || []).map((n, i) => <NewsItem key={i} n={n} />)}
                            {!newsLoading && news && !news.items.length && (
                                <p className="px-4 py-12 text-center text-slate-400 text-sm">
                                    No headlines available. {canPublish ? 'Add a source to get started.' : ''}
                                </p>
                            )}
                        </div>
                    </Panel>

                    {!!news?.feeds?.length && (
                        <p className="text-[11px] text-slate-400">
                            Sources: {news.feeds.map(f => f.label).join(' · ')}. Headlines are cached for 30 minutes.
                        </p>
                    )}
                </div>
            )}

            {/* ---------------- LINKS ---------------- */}
            {tab === 'links' && (
                <div className="space-y-6">
                    {canPublish && (
                        <div className="flex justify-end">
                            <button className={btn}
                                onClick={() => setLinkForm({ label: '', url: '', description: '', category: 'Reference', sort_order: 0 })}>
                                Add link
                            </button>
                        </div>
                    )}
                    {LINK_CATS.map(c => {
                        const items = links.filter(l => l.category === c);
                        if (!items.length) return null;
                        return (
                            <div key={c}>
                                <Eyebrow>{c}</Eyebrow>
                                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                                    {items.map(l => (
                                        <div key={l.link_id} className="border border-slate-200 rounded p-3 bg-white flex justify-between items-start gap-2">
                                            <div className="min-w-0">
                                                <a href={l.url} target="_blank" rel="noopener noreferrer"
                                                    className="font-medium text-indigo-600 hover:underline text-sm">{l.label}</a>
                                                {l.description && <p className="text-xs text-slate-500 mt-0.5 leading-relaxed">{l.description}</p>}
                                            </div>
                                            {canPublish && (
                                                <button onClick={() => remove('links', l.link_id, l.label)}
                                                    className="text-slate-300 hover:text-red-600 text-lg leading-none shrink-0"
                                                    aria-label={`Remove ${l.label}`}>×</button>
                                            )}
                                        </div>
                                    ))}
                                </div>
                            </div>
                        );
                    })}
                    {!links.length && <p className="text-center text-slate-400 py-12 text-sm">No links yet.</p>}
                </div>
            )}

            {/* ---------------- READERS ---------------- */}
            {openArticle && (
                <Modal open title={openArticle.title} onClose={() => setOpenArticle(null)} size="lg"
                    footer={<button onClick={() => setOpenArticle(null)} className={btnGhost}>Close</button>}>
                    <div className="text-[11px] uppercase tracking-wide text-slate-400 mb-3">
                        {openArticle.category}{openArticle.tags ? ` · ${openArticle.tags}` : ''}
                    </div>
                    <div className="doc-body"
                        dangerouslySetInnerHTML={{ __html: openArticle.body_html || '' }} />
                </Modal>
            )}
            {openAnn && (
                <Modal open title={openAnn.title} onClose={() => setOpenAnn(null)} size="lg"
                    footer={<button onClick={() => setOpenAnn(null)} className={btnGhost}>Close</button>}>
                    <div className="text-[11px] text-slate-400 mb-3 tabular-nums">
                        {fmtDate(openAnn.published_at)}{openAnn.author ? ` · ${openAnn.author}` : ''}
                    </div>
                    <div className="doc-body"
                        dangerouslySetInnerHTML={{ __html: openAnn.body_html || '' }} />
                </Modal>
            )}

            {/* ---------------- FEED MANAGER ---------------- */}
            {manageFeeds && (
                <Modal open title="Industry news sources" onClose={() => setManageFeeds(false)} size="md"
                    footer={<>
                        <button onClick={() => setManageFeeds(false)} className={btnGhost}>Close</button>
                        <button onClick={() => setFeedForm({ label: '', url: '', homepage: '', sort_order: 0 })} className={btn}>Add source</button>
                    </>}>
                    <p className="text-[13px] text-slate-500 mb-3">
                        RSS or Atom feed addresses. Some publishers block automated readers — if a source
                        keeps failing, that is usually why, and its homepage still belongs on Quick Links.
                    </p>
                    <div className="divide-y divide-slate-100 border border-slate-200 rounded">
                        {feeds.filter(f => f.is_active).map(f => (
                            <div key={f.feed_id} className="px-3 py-2.5 flex justify-between items-start gap-3">
                                <div className="min-w-0">
                                    <div className="font-medium text-sm text-slate-800">{f.label}</div>
                                    <div className="text-[11px] text-slate-400 truncate">{f.url}</div>
                                </div>
                                <button onClick={() => remove('feeds', f.feed_id, f.label)}
                                    className="text-sm text-red-600 hover:underline shrink-0">Remove</button>
                            </div>
                        ))}
                        {!feeds.filter(f => f.is_active).length && (
                            <p className="px-3 py-6 text-center text-slate-400 text-sm">No sources configured.</p>
                        )}
                    </div>
                </Modal>
            )}

            {/* ---------------- EDITORS ---------------- */}
            {annForm && (
                <Modal open title={annForm.announcement_id ? 'Edit notice' : 'New notice'} onClose={() => setAnnForm(null)} size="lg"
                    footer={<>
                        <button onClick={() => setAnnForm(null)} className={btnGhost}>Cancel</button>
                        <button onClick={() => save('ann')} disabled={saving} className={`${btn} disabled:bg-indigo-400`}>{saving ? 'Saving…' : 'Save'}</button>
                    </>}>
                    <div className="space-y-3">
                        <div><label className={label}>Title</label>
                            <input className={input} value={annForm.title} onChange={e => setAnnForm({ ...annForm, title: e.target.value })} /></div>
                        <div className="grid grid-cols-3 gap-3">
                            <div><label className={label}>Category</label>
                                <select className={input} value={annForm.category} onChange={e => setAnnForm({ ...annForm, category: e.target.value })}>
                                    {ANN_CATS.map(c => <option key={c}>{c}</option>)}
                                </select></div>
                            <div><label className={label}>Status</label>
                                <select className={input} value={annForm.status} onChange={e => setAnnForm({ ...annForm, status: e.target.value })}>
                                    <option value="draft">Draft</option><option value="published">Published</option><option value="archived">Archived</option>
                                </select></div>
                            <div className="flex items-end gap-2 pb-2">
                                <input id="pin" type="checkbox" checked={!!annForm.is_pinned}
                                    onChange={e => setAnnForm({ ...annForm, is_pinned: e.target.checked })} />
                                <label htmlFor="pin" className="text-sm text-slate-700">Pin to top</label>
                            </div>
                        </div>
                        <div><label className={label}>Message</label>
                            <textarea className={`${input} h-48 font-mono text-xs`} value={annForm.body_html}
                                onChange={e => setAnnForm({ ...annForm, body_html: e.target.value })}
                                placeholder="<p>Write the notice here. Basic HTML works: &lt;p&gt;, &lt;strong&gt;, &lt;ul&gt;&lt;li&gt;.</p>" /></div>
                    </div>
                </Modal>
            )}
            {artForm && (
                <Modal open title={artForm.article_id ? 'Edit guide' : 'New guide'} onClose={() => setArtForm(null)} size="lg"
                    footer={<>
                        <button onClick={() => setArtForm(null)} className={btnGhost}>Cancel</button>
                        <button onClick={() => save('art')} disabled={saving} className={`${btn} disabled:bg-indigo-400`}>{saving ? 'Saving…' : 'Save'}</button>
                    </>}>
                    <div className="space-y-3">
                        <div><label className={label}>Title</label>
                            <input className={input} value={artForm.title} onChange={e => setArtForm({ ...artForm, title: e.target.value })} /></div>
                        <div className="grid grid-cols-3 gap-3">
                            <div><label className={label}>Category</label>
                                <select className={input} value={artForm.category} onChange={e => setArtForm({ ...artForm, category: e.target.value })}>
                                    {ART_CATS.map(c => <option key={c}>{c}</option>)}
                                </select></div>
                            <div><label className={label}>Tags</label>
                                <input className={input} value={artForm.tags || ''} onChange={e => setArtForm({ ...artForm, tags: e.target.value })} placeholder="escrow, closing" /></div>
                            <div><label className={label}>Status</label>
                                <select className={input} value={artForm.status} onChange={e => setArtForm({ ...artForm, status: e.target.value })}>
                                    <option value="draft">Draft</option><option value="published">Published</option><option value="archived">Archived</option>
                                </select></div>
                        </div>
                        <div><label className={label}>Content</label>
                            <textarea className={`${input} h-64 font-mono text-xs`} value={artForm.body_html}
                                onChange={e => setArtForm({ ...artForm, body_html: e.target.value })} /></div>
                    </div>
                </Modal>
            )}
            {linkForm && (
                <Modal open title="Add quick link" onClose={() => setLinkForm(null)} size="md"
                    footer={<>
                        <button onClick={() => setLinkForm(null)} className={btnGhost}>Cancel</button>
                        <button onClick={() => save('link')} disabled={saving} className={`${btn} disabled:bg-indigo-400`}>{saving ? 'Saving…' : 'Save'}</button>
                    </>}>
                    <div className="space-y-3">
                        <div><label className={label}>Label</label>
                            <input className={input} value={linkForm.label} onChange={e => setLinkForm({ ...linkForm, label: e.target.value })} /></div>
                        <div><label className={label}>URL</label>
                            <input className={input} value={linkForm.url} onChange={e => setLinkForm({ ...linkForm, url: e.target.value })} placeholder="https://" /></div>
                        <div><label className={label}>Description</label>
                            <input className={input} value={linkForm.description || ''} onChange={e => setLinkForm({ ...linkForm, description: e.target.value })} /></div>
                        <div><label className={label}>Category</label>
                            <select className={input} value={linkForm.category} onChange={e => setLinkForm({ ...linkForm, category: e.target.value })}>
                                {LINK_CATS.map(c => <option key={c}>{c}</option>)}
                            </select></div>
                    </div>
                </Modal>
            )}
            {feedForm && (
                <Modal open title="Add news source" onClose={() => setFeedForm(null)} size="md"
                    footer={<>
                        <button onClick={() => setFeedForm(null)} className={btnGhost}>Cancel</button>
                        <button onClick={() => save('feed')} disabled={saving} className={`${btn} disabled:bg-indigo-400`}>{saving ? 'Saving…' : 'Save'}</button>
                    </>}>
                    <div className="space-y-3">
                        <div><label className={label}>Publication name</label>
                            <input className={input} value={feedForm.label} onChange={e => setFeedForm({ ...feedForm, label: e.target.value })} placeholder="Illinois REALTORS" /></div>
                        <div><label className={label}>Feed URL (RSS or Atom)</label>
                            <input className={input} value={feedForm.url} onChange={e => setFeedForm({ ...feedForm, url: e.target.value })} placeholder="https://example.com/feed/" /></div>
                        <div><label className={label}>Homepage (optional)</label>
                            <input className={input} value={feedForm.homepage || ''} onChange={e => setFeedForm({ ...feedForm, homepage: e.target.value })} placeholder="https://example.com/" /></div>
                    </div>
                </Modal>
            )}
        </div>
    );
};

export default IntranetPage;
